import React, { useState, useEffect } from 'react';
import type { Order, Customer, SmartpayTerminal, IntegrationSettings, SubBill } from '../../types';
import PrinterIcon from '../icons/PrinterIcon';
import { getSmartConnectService } from '../../lib/smartconnect';
import { getOrderTotals, getAmountPaid } from '../../lib/orderUtils';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';

interface OrderDetailsModalProps {
  order: Order;
  customer: Customer | null;
  terminals: SmartpayTerminal[];
  integrationSettings?: IntegrationSettings;
  onClose: () => void;
  onPrintReceipt: (order: Order) => void;
  onRefund?: (orderId: string, refundAmount: number, refundTransactionId: string) => void;
  onOverride?: (order: Order) => void;
  onRemake?: (order: Order) => void;
  onTransfer?: (order: Order) => void;
  onExtendBuffetTime?: (order: Order) => void;
  /** End buffet session early (customer finished before allocated time) */
  onCompleteBuffetOrder?: (order: Order) => void;
  /** When order has a balance due, call to open POS payment flow for this order */
  onPayBalance?: (order: Order) => void;
  /** Reprint a closed sub-bill receipt for one customer's slice. */
  onReprintSubBill?: (order: Order, subBill: SubBill) => void;
  /** Manager-only: refund this sub-bill (writes negative payment + restores paidQuantity). */
  onRefundSubBill?: (orderId: string, subBillId: string) => Promise<void> | void;
  isManager?: boolean;
}

function formatPaymentMethodLabel(method?: string, paymentGateway?: string): string {
  const m = (method || '').toLowerCase();
  if (m === 'cash') return 'Cash';
  if (m === 'card') return 'Card';
  if (m === 'card_online') {
    return paymentGateway === 'windcave' ? 'Card (online, Windcave)' : 'Card (online)';
  }
  if (m === 'account2account') return 'Bank transfer';
  return method ? method.replace(/_/g, ' ') : 'Payment';
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, customer, terminals, integrationSettings, onClose, onPrintReceipt, onRefund, onOverride, onRemake, onTransfer, onExtendBuffetTime, onCompleteBuffetOrder, onPayBalance, onReprintSubBill, onRefundSubBill, isManager }) => {
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundAmount, setRefundAmount] = useState<number | ''>('');
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>(terminals.find(t => t.isPaired)?.id || '');
  const [receiptEmail, setReceiptEmail] = useState(customer?.email || '');
  const [receiptEmailSent, setReceiptEmailSent] = useState(false);
  const [receiptEmailSending, setReceiptEmailSending] = useState(false);
  const { enabled: softKeyboardEnabled, openKeyboard, closeKeyboard } = useSoftKeyboard();

  const cardPayments = order.payments.filter(p => p.method === 'card');
  const cashPayments = order.payments.filter(p => p.method === 'cash');
  const firstCardWithTransaction = cardPayments.find(p => !!p.transactionId);
  const fallbackTransactionId = order.lastCardTransactionId;
  const { subtotal, taxAmount, totalAmount } = getOrderTotals(order);
  const amountPaid = getAmountPaid(order);
  const balanceDue = Math.round((totalAmount - amountPaid) * 100) / 100;
  const showBalanceDue = balanceDue > 0.01;
  // Show the refund panel only when fully paid (no balance due) with at least one payment (card or cash)
  // and only when a manager is viewing the order (cashiers cannot initiate refunds).
  const hasCardPayments = cardPayments.length > 0;
  const hasCashPayments = cashPayments.length > 0;
  const canRefund =
    !!isManager &&
    !showBalanceDue &&
    order.paymentStatus === 'paid' &&
    (hasCardPayments || hasCashPayments) &&
    order.status !== 'refunded' &&
    order.status !== 'cancelled';

  const pairedTerminals = terminals.filter(t => t.isPaired);

  const canSendEmailReceipt =
    order.paymentStatus === 'paid' &&
    order.status !== 'cancelled' &&
    order.status !== 'refunded';

  useEffect(() => {
    setReceiptEmail(customer?.email || '');
    setReceiptEmailSent(false);
  }, [customer?.email, order.id]);

  useEffect(() => {
    if (!selectedTerminalId && pairedTerminals.length > 0) {
      setSelectedTerminalId(pairedTerminals[0].id);
    } else if (selectedTerminalId && !pairedTerminals.some(t => t.id === selectedTerminalId)) {
      setSelectedTerminalId(pairedTerminals[0]?.id || '');
    }
  }, [pairedTerminals, selectedTerminalId]);

  const handleRefund = async () => {
    if (!canRefund || !onRefund) return;

    const amount = typeof refundAmount === 'number' ? refundAmount : parseFloat(refundAmount.toString());
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid refund amount.');
      return;
    }

    const totalCardPaid = cardPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalCashPaid = cashPayments.reduce((sum, p) => sum + p.amount, 0);

    // If there are card payments, card refunds must not exceed card total.
    if (hasCardPayments && amount > totalCardPaid) {
      alert(`Refund amount cannot exceed the total card payment of $${totalCardPaid.toFixed(2)}.`);
      return;
    }
    // If there are no card payments (cash-only order), ensure refund does not exceed total cash paid.
    if (!hasCardPayments && hasCashPayments && amount > totalCashPaid) {
      alert(`Refund amount cannot exceed the total cash payment of $${totalCashPaid.toFixed(2)}.`);
      return;
    }

    // Cash-only refund path: record a negative cash payment without hitting Smartpay.
    if (!hasCardPayments && hasCashPayments) {
      if (!window.confirm(`Record a CASH refund of $${amount.toFixed(2)} for this order? This will mark the order as refunded in the POS.`)) {
        return;
      }
      onRefund(order.id, amount, 'cash');
      alert(`Cash refund of $${amount.toFixed(2)} recorded.`);
      setRefundAmount('');
      onClose();
      return;
    }

    const terminal = pairedTerminals.find(t => t.id === selectedTerminalId);
    if (!terminal || !terminal.isPaired) {
      alert('Please select a paired terminal for the refund.');
      return;
    }

    // Find the original transaction ID (use the first card payment's transaction ID)
    // Check all card payments for transaction IDs, not just the first one
    const allTransactionIds = cardPayments
      .map(p => p.transactionId)
      .filter((id): id is string => !!id);
    
    const originalTransactionId =
      allTransactionIds[0] || // First card payment with transaction ID
      firstCardWithTransaction?.transactionId || 
      fallbackTransactionId ||
      order.lastCardTransactionId; // Also check order-level storage

    // Log transaction ID lookup for debugging
    console.log('🔍 Refund transaction ID lookup:', {
      orderId: order.id,
      cardPaymentsCount: cardPayments.length,
      allTransactionIds,
      firstCardWithTransactionId: firstCardWithTransaction?.transactionId,
      fallbackTransactionId,
      orderLastCardTransactionId: order.lastCardTransactionId,
      finalTransactionId: originalTransactionId,
      paymentDetails: cardPayments.map(p => ({
        method: p.method,
        amount: p.amount,
        transactionId: p.transactionId,
        timestamp: p.timestamp
      }))
    });

    // If we don't have a transaction ID, we can't call Smartpay to do an online refund.
    // Offer to record a manual refund instead (assuming the refund is done directly on the terminal).
    if (!originalTransactionId) {
      const confirmManual = window.confirm(
        `Original card transaction ID was not stored for this order.\n\n` +
        `You can still record a refund of $${amount.toFixed(
          2
        )} in the POS, but you must process the refund manually on the terminal.\n\n` +
        `Do you want to record this refund as a manual refund?`
      );
      if (!confirmManual) return;

      onRefund(order.id, amount, 'manual');
      alert(`Manual refund of $${amount.toFixed(
        2
      )} recorded. Please ensure the refund is processed on the terminal as well.`);
      setRefundAmount('');
      onClose();
      return;
    }

    if (!window.confirm(`Process refund of $${amount.toFixed(2)} via terminal?`)) {
      return;
    }

    setIsRefunding(true);
    try {
      const service = getSmartConnectService(
        integrationSettings?.smartpay?.apiHost,
        integrationSettings?.smartpay?.posVendorName,
        integrationSettings?.smartpay?.posBusinessName
      );

      const saleData = service.convertOrderToSaleData(order, integrationSettings?.businessInfo);
      // Mark as refund in sale data
      saleData.saleData.returnFor = order.id;

      // Handle delayed transaction callback
      const onDelayed = () => {
        // Could show a message to user that refund is taking longer
        console.log('Refund transaction is taking longer than usual...');
      };

      console.log('🔄 Processing refund via Smartpay:', {
        orderId: order.id,
        refundAmount: amount,
        originalTransactionId,
        terminalRegisterId: terminal.registerId,
        terminalName: terminal.name
      });

      const result = await service.refund(
        terminal.registerId,
        amount,
        originalTransactionId,
        saleData,
        onDelayed
      );

      console.log('🔄 Refund result:', {
        success: result.success,
        message: result.message,
        transactionId: result.transactionId,
        outcome: result.outcome
      });

      if (result.success && result.transactionId) {
        onRefund(order.id, amount, result.transactionId);
        alert(`Refund of $${amount.toFixed(2)} processed successfully.`);
        setRefundAmount('');
        onClose();
      } else {
        alert(`Refund failed: ${result.message}`);
      }
    } catch (error: any) {
      console.error('Refund error:', error);
      alert(`Refund error: ${error.message || 'Unknown error'}`);
    } finally {
      setIsRefunding(false);
    }
  };

  const handleSendReceiptEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(receiptEmail.trim())) {
      alert('Please enter a valid email address.');
      return;
    }
    const email = receiptEmail.trim();
    try {
      setReceiptEmailSending(true);
      setReceiptEmailSent(false);
      const { emailService } = await import('../../lib/email');
      const customerName =
        customer?.name ||
        order.customerName ||
        order.deliveryInfo?.customerName ||
        'Valued Customer';
      const customerLoyaltyPoints = customer?.loyaltyPoints || 0;
      const result = await emailService.sendOrderReceipt(
        email,
        order,
        customerName,
        integrationSettings?.businessInfo,
        customerLoyaltyPoints
      );
      if (result.success) {
        setReceiptEmailSent(true);
        setTimeout(() => setReceiptEmailSent(false), 5000);
      } else {
        alert(`Failed to send email: ${result.error || 'Unknown error'}`);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error sending receipt email:', error);
      alert(`Failed to send email: ${message}`);
    } finally {
      setReceiptEmailSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden my-auto"
      >
        <header className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
            <p className="text-sm text-gray-500">{order.id}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
        </header>
        <main className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">Customer</p>
              <p className="font-semibold">{order.customerName || order.deliveryInfo?.customerName || customer?.name || 'Guest'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Order Type</p>
              <p className="font-semibold capitalize">{order.orderType}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-semibold">
                {(() => {
                  let orderDate: Date;
                  if (typeof order.timestamp === 'number') {
                    orderDate = new Date(order.timestamp);
                  } else if (order.timestamp instanceof Date) {
                    orderDate = order.timestamp;
                  } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                    orderDate = new Date((order.timestamp as any).seconds * 1000);
                  } else {
                    return 'Invalid Date';
                  }
                  if (isNaN(orderDate.getTime())) {
                    return 'Invalid Date';
                  }
                  return orderDate.toLocaleString('en-NZ', {
                    timeZone: 'Pacific/Auckland',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  });
                })()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Payment Status</p>
              <p className={`font-semibold capitalize ${order.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                {order.paymentStatus}
              </p>
            </div>
          </div>
          
          <h3 className="font-semibold text-gray-700 mb-2 border-b pb-1">Items</h3>
          <ul className="mb-6">
            {(order.items ?? []).filter(item => !item.removed).map(item => (
              <li key={item.lineId ?? item.id} className="flex justify-between py-1">
                <span className="text-gray-600">{item.quantity}x {item.name}</span>
                <span className="text-gray-800">${(item.price * item.quantity).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          
          <div className="border-t pt-4 space-y-2">
            <p className="flex justify-between"><span>Subtotal (ex. GST):</span> <span>${subtotal.toFixed(2)}</span></p>
            <p className="flex justify-between"><span>GST (15%):</span> <span>${taxAmount.toFixed(2)}</span></p>
            {order.appliedReward && (
              <p className="flex justify-between text-green-600">
                <span>Discount ({order.appliedReward.name}):</span> 
                <span>-${(order.appliedReward.discountValue || 0).toFixed(2)}</span>
              </p>
            )}
            {order.discountMethod === 'first-table' && (order.firstTableDiscountAmount ?? 0) > 0 && (
              <p className="flex justify-between text-green-600">
                <span>First Table discount:</span>{' '}
                <span>-${(order.firstTableDiscountAmount ?? 0).toFixed(2)}</span>
              </p>
            )}
            {order.discountMethod !== 'first-table' && (order.manualDiscount ?? 0) > 0 && (
              <p className="flex justify-between text-green-600">
                <span>Manual discount:</span> <span>-${(order.manualDiscount ?? 0).toFixed(2)}</span>
              </p>
            )}
            {order.orderType === 'delivery' && (order.deliveryCharge ?? 0) > 0 && (
              <p className="flex justify-between text-pink-700">
                <span>Delivery charge (incl. GST):</span>
                <span>+${(order.deliveryCharge ?? 0).toFixed(2)}</span>
              </p>
            )}
            <p className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                <span>Total:</span> <span>${totalAmount.toFixed(2)}</span>
            </p>
            {order.payments && order.payments.length > 0 && (
              <>
                <p className="flex justify-between text-sm text-gray-600 mt-1">
                  <span>Amount paid:</span> <span>${amountPaid.toFixed(2)}</span>
                </p>
                {showBalanceDue && (
                  <p className="flex justify-between font-semibold text-amber-700">
                    <span>Balance to pay:</span> <span>${balanceDue.toFixed(2)}</span>
                  </p>
                )}
              </>
            )}
          </div>
          
          {order.payments.length > 0 && (
            <div className="mt-6">
               <h3 className="font-semibold text-gray-700 mb-2 border-b pb-1">Payments</h3>
               {order.payments.map((p, i) => (
                   <div key={i} className="mb-2">
                     <p className="flex justify-between text-sm">
                       <span>
                         {formatPaymentMethodLabel(p.method, p.paymentGateway)}{p.subBillName ? ` (${p.subBillName})` : ''}:
                       </span>
                       <span>${p.amount.toFixed(2)}</span>
                     </p>
                     {p.tip && p.tip > 0 && (
                       <p className="flex justify-between text-xs text-pink-600 ml-4">
                         <span>Tip:</span> <span>${p.tip.toFixed(2)}</span>
                       </p>
                     )}
                   </div>
               ))}
               {order.totalTip && order.totalTip > 0 && (
                 <div className="mt-3 pt-2 border-t border-gray-200">
                   <p className="flex justify-between font-semibold text-sm">
                     <span>Total Tips:</span> <span className="text-pink-600">${order.totalTip.toFixed(2)}</span>
                   </p>
                 </div>
               )}
            </div>
          )}

          {order.subBills && order.subBills.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-2 border-b pb-1">
                Split bills ({order.subBills.length})
              </h3>
              <ul className="space-y-2">
                {order.subBills.map((sb) => {
                  const itemCount = sb.itemAllocations.reduce((s, a) => s + a.quantity, 0);
                  const sbPayments = order.payments.filter((p) => p.id && sb.paymentIds.includes(p.id));
                  return (
                    <li key={sb.id} className="rounded border border-gray-200 bg-gray-50 p-2 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{sb.name || 'Guest'}</p>
                          <p className="text-xs text-gray-600">
                            {itemCount} item{itemCount === 1 ? '' : 's'} · ${sb.total.toFixed(2)} · {sb.discountAllocationMode === 'proportional' ? 'proportional discount' : 'remainder discount'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Paid via:{' '}
                            {sbPayments.length === 0
                              ? '—'
                              : sbPayments
                                  .map((p) => `${formatPaymentMethodLabel(p.method, p.paymentGateway)} $${p.amount.toFixed(2)}`)
                                  .join(', ')}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {onReprintSubBill && (
                            <button
                              type="button"
                              onClick={() => onReprintSubBill(order, sb)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                            >
                              <PrinterIcon className="w-4 h-4" /> Reprint
                            </button>
                          )}
                          {isManager && onRefundSubBill && (
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Refund "${sb.name || 'Guest'}" sub-bill of $${sb.total.toFixed(2)}?\n\nThe items will become available to re-pay. The cash/card refund itself must be handled out-of-band.`)) {
                                  void onRefundSubBill(order.id, sb.id);
                                }
                              }}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-900"
                            >
                              Refund ${sb.total.toFixed(2)}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {canRefund && onRefund && (
            <div className="mt-6 mb-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Process Refund</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Refund Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={(
                      hasCardPayments
                        ? cardPayments.reduce((sum, p) => sum + p.amount, 0)
                        : cashPayments.reduce((sum, p) => sum + p.amount, 0)
                    ).toFixed(2)}
                    value={refundAmount === '' ? '' : refundAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRefundAmount(value === '' ? '' : parseFloat(value) || '');
                    }}
                    placeholder={`Max: $${(
                      hasCardPayments
                        ? cardPayments.reduce((sum, p) => sum + p.amount, 0)
                        : cashPayments.reduce((sum, p) => sum + p.amount, 0)
                    ).toFixed(2)}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 text-sm"
                  />
                </div>
                {/* Card refunds require a paired terminal; cash refunds do not. */}
                {hasCardPayments && pairedTerminals.length > 0 ? (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">Select Terminal</p>
                    <div className="flex flex-wrap gap-2">
                      {pairedTerminals.map(t => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setSelectedTerminalId(t.id)}
                          className={`px-3 py-2 border rounded-md text-sm font-semibold transition ${
                            selectedTerminalId === t.id
                              ? 'border-pink-500 bg-pink-50 text-pink-700'
                              : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : hasCardPayments ? (
                  <p className="text-xs text-gray-500">No paired terminals available</p>
                ) : (
                  <p className="text-xs text-gray-500">Cash refund will be recorded in POS only.</p>
                )}
                <button
                  style={{ marginTop: '0.75rem' }}
                  onClick={handleRefund}
                  disabled={isRefunding || !refundAmount || (hasCardPayments && !selectedTerminalId)}
                  className="relative z-10 w-full bg-red-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                  type="button"
                >
                  {isRefunding ? 'Processing Refund...' : hasCardPayments ? 'Process Card Refund' : 'Record Cash Refund'}
                </button>
              </div>
            </div>
          )}

        </main>
        <footer className="p-4 border-t bg-gray-50 space-y-3 flex-shrink-0">
          {canSendEmailReceipt && (
            <div className="text-left space-y-2 pb-1">
              <label htmlFor="order-details-receipt-email" className="block text-sm font-medium text-gray-700">
                Email receipt
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  id="order-details-receipt-email"
                  value={receiptEmail}
                  readOnly={softKeyboardEnabled}
                  onFocus={() =>
                    openKeyboard({
                      id: 'order-details-receipt-email',
                      type: 'alphanumeric',
                      value: receiptEmail,
                      label: 'Email',
                      onChange: setReceiptEmail,
                    })
                  }
                  onChange={(e) => {
                    if (!softKeyboardEnabled) setReceiptEmail(e.target.value);
                  }}
                  placeholder={customer?.email ? `${customer.name}'s email` : 'customer@example.com'}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                />
                <button
                  type="button"
                  onClick={handleSendReceiptEmail}
                  disabled={receiptEmailSending}
                  className="shrink-0 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors px-4 py-2 text-sm disabled:opacity-50"
                >
                  {receiptEmailSending ? 'Sending…' : 'Send'}
                </button>
              </div>
              {receiptEmailSent && (
                <p className="text-green-600 text-sm">Receipt sent to {receiptEmail.trim()}!</p>
              )}
            </div>
          )}
          <div className="flex justify-between items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {onPayBalance && showBalanceDue && (
                <button
                  onClick={() => { onPayBalance(order); onClose(); }}
                  className="bg-green-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-600 transition-colors text-sm"
                  type="button"
                >
                  Pay balance (${balanceDue.toFixed(2)})
                </button>
              )}
              {isManager && onOverride && (order.paymentStatus === 'unpaid' || showBalanceDue) && order.status !== 'comped' && (
                <button 
                  onClick={() => onOverride(order)}
                  className="bg-red-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm"
                >
                  Override (Comp)
                </button>
              )}
              {onRemake && (order.paymentStatus === 'paid' || order.status === 'served' || order.status === 'ready') && (
                <button 
                  onClick={() => onRemake(order)}
                  className="bg-purple-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors text-sm"
                >
                  Remake Order
                </button>
              )}
              {onCompleteBuffetOrder && order.orderType === 'buffet' && order.status === 'buffet' && (
                <button 
                  onClick={() => onCompleteBuffetOrder(order)}
                  className="bg-purple-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  End buffet session
                </button>
              )}
              {onExtendBuffetTime && order.orderType === 'buffet' && order.buffetEndTime && (
                <button 
                  onClick={() => onExtendBuffetTime(order)}
                  className="bg-purple-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors text-sm"
                >
                  Extend Buffet Time
                </button>
              )}
              {onTransfer && (order.orderType === 'dine-in' || order.orderType === 'buffet') && order.paymentStatus === 'unpaid' && order.status !== 'comped' && (
                <button 
                  onClick={() => onTransfer(order)}
                  className="bg-orange-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors text-sm"
                >
                  Transfer Order
                </button>
              )}
            </div>
            <button 
              onClick={() => onPrintReceipt(order)}
              className="bg-blue-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center"
            >
              <PrinterIcon className="w-5 h-5 mr-2" />
              Print Receipt
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default OrderDetailsModal;